if (!customElements.get('user-map-gallery')) {
  class UserMapGallery extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.slidesByCategory = new Map();
      this.activeCategory = '';
      this.pendingBlockId = '';
      this.onClick = this.handleClick.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.swiperEl = this.querySelector('[data-umg-swiper]');
      this.stashEl = this.querySelector('[data-umg-stash]');
      this.prevEl = this.querySelector('.swiper-button-prev');
      this.nextEl = this.querySelector('.swiper-button-next');

      if (!this.swiperEl || !this.stashEl) return;

      this.indexSlides();
      const firstCategory = (this.dataset.defaultCategory || '').trim() || this.getFirstCategory() || '';
      this.activeCategory = firstCategory;
      const resolved = this.renderCategory(firstCategory);
      if (resolved && resolved !== firstCategory) this.activeCategory = resolved;
      this.updateHotspots(this.activeCategory);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.initSwiper();
      if (Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }

    ensureSwiperLoaded() {
      return new Promise((resolve) => {
        if (window.Swiper) return resolve(true);
        let tries = 0;
        const tick = () => {
          tries += 1;
          if (window.Swiper) return resolve(true);
          if (tries > 180) return resolve(false);
          window.requestAnimationFrame(tick);
        };
        tick();
      });
    }

    indexSlides() {
      const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      const slides = Array.from(wrapper.querySelectorAll('.user-map-gallery__slide'));
      slides.forEach((slide) => {
        const cat = (slide.dataset.category || '').trim();
        if (!this.slidesByCategory.has(cat)) this.slidesByCategory.set(cat, []);
        this.slidesByCategory.get(cat).push(slide);
        this.stashEl.appendChild(slide);
      });
    }

    getFirstCategory() {
      const hotspot = this.querySelector('[data-umg-hotspot][data-umg-category]');
      if (hotspot) return (hotspot.dataset.umgCategory || '').trim();
      const keys = Array.from(this.slidesByCategory.keys());
      return keys.length ? keys[0] : '';
    }

    renderCategory(category) {
      const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) return '';

      Array.from(wrapper.children).forEach((child) => this.stashEl.appendChild(child));

      const key = (category || '').trim();
      if (!key || key.toLowerCase() === 'all') {
        const allSlides = [];
        this.slidesByCategory.forEach((arr) => {
          arr.forEach((slide) => allSlides.push(slide));
        });
        allSlides.forEach((slide) => wrapper.appendChild(slide));
        return 'all';
      }

      let resolvedCategory = category;
      let slides = this.slidesByCategory.get(resolvedCategory) || [];
      if (!slides.length) {
        const fallback = Array.from(this.slidesByCategory.keys()).find((key) => (this.slidesByCategory.get(key) || []).length);
        resolvedCategory = fallback || resolvedCategory;
        slides = this.slidesByCategory.get(resolvedCategory) || [];
      }

      slides.forEach((slide) => wrapper.appendChild(slide));
      return resolvedCategory;
    }

    updateHotspots(activeCategory) {
      const hotspots = Array.from(this.querySelectorAll('[data-umg-hotspot][data-umg-category]'));
      hotspots.forEach((el) => {
        const cat = (el.dataset.umgCategory || '').trim();
        const isActive = cat === activeCategory;
        el.classList.toggle('is-active', isActive);
        el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    setSlidePlaying(slideEl, isPlaying) {
      slideEl.classList.toggle('is-playing', isPlaying);
      slideEl.classList.toggle('is-paused', !isPlaying);
      const btn = slideEl.querySelector('[data-umg-toggle]');
      if (btn) btn.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    }

    pauseAndResetAllVideos(exceptVideo) {
      const slides = Array.from(this.swiperEl.querySelectorAll('.user-map-gallery__slide'));
      slides.forEach((slide) => {
        const video = slide.querySelector('[data-umg-video]');
        this.setSlidePlaying(slide, false);
        if (!video) return;
        try {
          video.pause();
        } catch (e) {}
        if (video !== exceptVideo) {
          try {
            video.currentTime = 0;
          } catch (e) {}
        }
        video.preload = 'metadata';
      });
    }

    getActiveSlide() {
      return this.swiperEl.querySelector('.swiper-slide-active');
    }

    updateNavVisibility() {
      if (!this.prevEl || !this.nextEl) return;
      const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) return;
      const count = wrapper.children.length;
      const hide = count < 4;
      this.prevEl.classList.toggle('is-hidden', hide);
      this.nextEl.classList.toggle('is-hidden', hide);
    }

    async playActiveVideo({ resetTime }) {
      const activeSlide = this.getActiveSlide();
      if (!activeSlide) return;
      const video = activeSlide.querySelector('[data-umg-video]');
      if (!video || video.tagName !== 'VIDEO') return;

      video.preload = 'auto';
      if (resetTime) {
        try {
          video.currentTime = 0;
        } catch (e) {}
      }

      try {
        await video.play();
        this.setSlidePlaying(activeSlide, true);
      } catch (e) {
        this.setSlidePlaying(activeSlide, false);
      }
    }

    onSlideChange(isInit) {
      const activeSlide = this.getActiveSlide();
      const activeVideo = activeSlide ? activeSlide.querySelector('[data-umg-video]') : null;
      const autoPlayEnabled = (this.swiperEl.dataset.autoPlay || '') === 'true';

      this.pauseAndResetAllVideos(activeVideo);
      if (activeSlide) this.setSlidePlaying(activeSlide, false);

      if (!isInit) {
        const activeCat = this.activeCategory;
        this.updateHotspots(activeCat);
      }

      if (autoPlayEnabled) this.playActiveVideo({ resetTime: true });
      this.updateNavVisibility();
    }

    async initSwiper() {
      const ok = await this.ensureSwiperLoaded();
      if (!ok) return;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper || wrapper.children.length === 0) return;

      const slideCount = wrapper.children.length;
      const shouldCenter = slideCount === 1;
      this.swiperEl.classList.toggle('umg-single', shouldCenter);

      // Determine initial slide for desktop to center the second item
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const initialSlide = isDesktop && slideCount >= 3 ? 1 : 0;

      const config = {
        initialSlide,
        centeredSlides: shouldCenter,
        centeredSlidesBounds: shouldCenter,
        grabCursor: true,
        loop: false,
        slidesPerView: 1,
        spaceBetween: 24,
        watchSlidesProgress: true,
        navigation: {
          nextEl: this.nextEl,
          prevEl: this.prevEl
        },
        breakpoints: {
          750: {
            slidesPerView: 2,
            spaceBetween: 15
          },
          990: {
            slidesPerView: 3,
            spaceBetween: 20,
            centeredSlides: true
          }
        },
        on: {
          init: () => {
            if (this.pendingBlockId) {
              const target = this.querySelector(`[data-umg-block-id="${this.pendingBlockId}"]`);
              this.pendingBlockId = '';
              if (target) {
                const idx = Array.from(this.swiper.slides).indexOf(target);
                if (idx >= 0) this.swiper.slideTo(idx, 0);
              }
            }
            this.onSlideChange(true);
            this.updateNavVisibility();
          },
          slideChangeTransitionStart: () => this.onSlideChange(false),
          resize: () => this.onSlideChange(false)
        }
      };

      this.swiper = new window.Swiper(this.swiperEl, config);
    }

    handleBlockSelect(event) {
      const blockId = event.detail.blockId;
      const sectionId = event.detail.sectionId;

      if (sectionId !== this.dataset.sectionId) return;

      const slide = this.querySelector(`[data-umg-block-id="${blockId}"]`);
      if (!slide) return;

      const cat = (slide.dataset.category || '').trim();
      if (cat && cat !== this.activeCategory) {
        this.pendingBlockId = blockId;
        this.setCategory(cat);
        return;
      }

      if (!this.swiper) return;
      const idx = Array.from(this.swiper.slides).indexOf(slide);
      if (idx >= 0) this.swiper.slideTo(idx);
    }

    async setCategory(category) {
      const nextCategory = (category || '').trim();
      if (!nextCategory || nextCategory === this.activeCategory) return;

      this.activeCategory = nextCategory;
      this.updateHotspots(nextCategory);

      this.pauseAndResetAllVideos();
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      const resolved = this.renderCategory(nextCategory);
      if (resolved && resolved !== nextCategory) {
        this.activeCategory = resolved;
        this.updateHotspots(resolved);
      }

      this.updateNavVisibility();
      await this.initSwiper();
    }

    async handleToggle(buttonEl) {
      const slide = buttonEl.closest('.user-map-gallery__slide');
      if (!slide) return;

      const isActive = slide.classList.contains('swiper-slide-active');

      const video = slide.querySelector('[data-umg-video]');
      if (!video || video.tagName !== 'VIDEO') return;

      if (video.paused || video.ended) {
        this.pauseAndResetAllVideos(video);
        video.preload = 'auto';
        try {
          await video.play();
          this.setSlidePlaying(slide, true);
        } catch (e) {
          this.setSlidePlaying(slide, false);
        }
      } else {
        try {
          video.pause();
        } catch (e) {}
        this.setSlidePlaying(slide, false);
      }
    }

    handleHotspot(hotspotEl) {
      const category = (hotspotEl.dataset.umgCategory || '').trim();
      if (!category) return;
      this.setCategory(category);
    }

    handleClick(evt) {
      const toggle = evt.target.closest('[data-umg-toggle]');
      if (toggle && this.contains(toggle)) {
        evt.preventDefault();
        this.handleToggle(toggle);
        return;
      }

      const slide = evt.target.closest('.user-map-gallery__slide');
      if (slide && this.contains(slide)) {
        const video = slide.querySelector('[data-umg-video]');
        if (video) {
          const btn = slide.querySelector('[data-umg-toggle]');
          if (btn) {
            evt.preventDefault();
            this.handleToggle(btn);
            return;
          }
        }
      }

      const hotspot = evt.target.closest('[data-umg-hotspot][data-umg-category]');
      if (hotspot && this.contains(hotspot)) {
        evt.preventDefault();
        this.handleHotspot(hotspot);
      }
    }

    handleKeyDown(evt) {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
      const hotspot = evt.target.closest('[data-umg-hotspot][data-umg-category]');
      if (!hotspot || !this.contains(hotspot)) return;
      evt.preventDefault();
      this.handleHotspot(hotspot);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      this.pauseAndResetAllVideos();
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (Shopify && Shopify.designMode) {
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }
  }

  customElements.define('user-map-gallery', UserMapGallery);
}
